import { ref } from 'vue'
import { useWebSocket } from './useWebSocket'

export interface SyncProgressEvent {
  type: 'sync-progress'
  taskCode: string
  bizDate: string
  syncBatchNo: string
  stage: 'FETCHING' | 'SAVING' | 'ETL' | 'VALIDATING' | 'COMPLETED' | 'FAILED'
  progressPct: number
  message: string
  detail: Record<string, any>
  timestamp: number
}

/**
 * 同步进度实时监听 composable
 *
 * 连接后端 WebSocket (/ws/sync-progress) 获取同步任务实时进度。
 * 自动在页面卸载时断开连接。
 *
 * @example
 * const { currentProgress, isConnected } = useSyncProgress()
 * watch(currentProgress, (event) => {
 *   if (event?.stage === 'COMPLETED') {
 *     ElNotification.success('数据同步完成')
 *     refreshTable()
 *   }
 * })
 */
export function useSyncProgress() {
  const currentProgress = ref<SyncProgressEvent | null>(null)
  const isConnected = ref(false)
  const progressHistory = ref<SyncProgressEvent[]>([])

  const { status, connect, onMessage } = useWebSocket({
    endpoint: '/ws/sync-progress',
    autoConnect: true,
  })

  // 监听连接状态
  const unwatchStatus = (() => {
    // watch-like effect
    const check = () => {
      isConnected.value = status.value === 'connected'
    }
    const timer = setInterval(check, 500)
    return () => clearInterval(timer)
  })()

  // 注册消息处理
  const unregister = onMessage((data: any) => {
    if (data.type === 'sync-progress') {
      const event = data as SyncProgressEvent
      currentProgress.value = event
      progressHistory.value.unshift(event)
      // 只保留最近 100 条
      if (progressHistory.value.length > 100) {
        progressHistory.value = progressHistory.value.slice(0, 100)
      }
    }
  })

  function clearHistory() {
    progressHistory.value = []
    currentProgress.value = null
  }

  // 注册全局清理（useWebSocket 已在 onUnmounted 中处理 disconnect）
  addEventListener('beforeunload', () => {
    unregister()
    unwatchStatus()
  })

  return {
    /** 当前最新的同步进度事件 */
    currentProgress,
    /** WebSocket 是否已连接 */
    isConnected,
    /** 进度历史记录(最近100条) */
    progressHistory,
    /** 清空历史 */
    clearHistory,
    /** 手动重连 */
    reconnect: connect,
  }
}
